import React, { Fragment, useEffect, useRef, useState, useCallback, useContext, useMemo } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Appfooter from "../components/Appfooter";
import Popupchat from "../components/Popupchat";
import PeopleCard from "../components/PeopleCard";
import { useAPI } from "../context/ApiContext";
import { SnackbarContext } from "../context/SnackbarContext";
import { useTranslation } from "react-i18next";

const DEFAULT_AVATAR = "/assets/images/default_profile.png";

const useQuery = () => new URLSearchParams(useLocation().search);

const Connections = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const q = useQuery();
  const { newSnack } = useContext(SnackbarContext);

  const {
    isAuthenticated,
    me,
    profile,               // 自分のプロフ（ヘッダーに使う用）
    getProfile,            // 指定ユーザーのプロフを取得
    getFollowing,          // /users/:id/following/?count=
    getFollowers,          // /users/:id/followers/?count=
  } = useAPI();

  // URL パラメータ
  const userIdFromUrl = q.get("user_id") || me?.userId || "";
  const tabFromUrl = q.get("tab") === "followers" ? "followers" : "following"; // デフォは following

  // タブ制御
  const [tab, setTab] = useState(tabFromUrl); // "following" | "followers"
  useEffect(() => setTab(tabFromUrl), [tabFromUrl]);

  // 対象ユーザー（自分以外も見れる）
  const [subject, setSubject] = useState(null); // { nickname, image, created_at, ... }
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!userIdFromUrl) return setSubject(null);
      try {
        const data = await getProfile(userIdFromUrl);
        if (mounted) setSubject(data);
      } catch {
        if (mounted) setSubject(null);
      }
    };
    run();
    return () => { mounted = false; };
  }, [userIdFromUrl, getProfile]);

  // リスト状態
  const [items, setItems] = useState([]);     // ユーザー配列（API 返却は { profile: {...} } or プロフ直値の想定）
  const [page, setPage] = useState(0);        // count パラメータ
  const [isLoading, setIsLoading] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const loaderRef = useRef(null);

  // ★ 追加：最新フラグを保持する refs（fetchPage を安定化させる）
  const isLoadingRef = useRef(false);
  const noMoreRef = useRef(false);
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { noMoreRef.current = noMore; }, [noMore]);
  // ★ 追加：重複呼び出し防止（同じキーのリクを一度に1本だけ）
  const inflightRef = useRef(new Set());
    // ★ 初回ページ（p=0）が完了するまで無限スクロールを止める
  const initialLoadedRef = useRef(false);

  // タブ or 対象ユーザーが変わったらリセット
  useEffect(() => {
    setItems([]);
    setPage(0);
    setNoMore(false);
    initialLoadedRef.current = false;          // ★ 初回未完了に戻す
    inflightRef.current = new Set();           // ★ 古いキーを破棄して安全側
        // ★ ここで直ちに強制ロード（前タブの isLoading に邪魔されない）
    const uid = userIdFromUrl;
    const nextTab = tabFromUrl; // または tab でもOK（このエフェクトの依存が [tab, userIdFromUrl] なので同値）
    if (uid) {
      // microtaskで呼んで、上の state リセットが反映された後に実行
      Promise.resolve().then(() => fetchPage(0, uid, nextTab, true));
    }
  }, [tab, userIdFromUrl]);

  // データ取得
  // ★ 修正：依存は“安定なもの”だけ。isLoading/noMore は ref から読む
  const fetchPage = useCallback(async (p, uid, whichTab, force = false) => {
    if (!uid) return;
     // ★ 初回 p=0 を「切替直後に強制ロード」したい時はガードをスキップ
     if (!force) {
       if (isLoadingRef.current || noMoreRef.current) return;
     }
    const key = `${uid}:${whichTab}:${p}`;
    if (inflightRef.current.has(key)) return; // 二重実行ガード
    inflightRef.current.add(key);
    setIsLoading(true);
    try {
      const api = whichTab === "followers" ? getFollowers : getFollowing;
      const res = await api(uid, { count: p });
      const list = Array.isArray(res?.res) ? res.res : (Array.isArray(res) ? res : []);
      if (!list.length) {
        setNoMore(true);
      } else {
        setItems(prev => (p === 0 ? list : [...prev, ...list]));
        if (p === 0) initialLoadedRef.current = true; // ★ 初回ページ到達
      }
    } catch (e) {
      newSnack("error", t("Connections1") || "Failed to load");
      setNoMore(true);
    } finally {
      inflightRef.current.delete(key);
      setIsLoading(false);
    }
    }, [getFollowers, getFollowing, newSnack, t]);

  // 初回 & ページング
  // ★ 依存を page / userId / tab に限定。fetchPage は安定化済み
  useEffect(() => {
    fetchPage(page, userIdFromUrl, tab);
  }, [page, userIdFromUrl, tab, fetchPage]);


  // 無限スクロール
  useEffect(() => {
    const io = new IntersectionObserver(([entry]) => {
    if (
      entry.isIntersecting &&
      initialLoadedRef.current &&           // ★ 初回が終わってから進める
      !isLoadingRef.current &&              // refで最新を見る
      !noMoreRef.current
    ) {
      setPage(v => v + 1);
    }
    }, { root: null, rootMargin: "0px", threshold: 1.0 });
    const el = loaderRef.current;
    if (el) io.observe(el);
    return () => io.disconnect();
}, []); // ★ IOは一度だけ作る（最新状態はrefで読む）

  // タブリンク（URL と状態を同期）
  const setTabAndPush = (next) => {
    const search = new URLSearchParams();
    if (userIdFromUrl) search.set("user_id", userIdFromUrl);
    search.set("tab", next);
    history.push(`/connections?${search.toString()}`);
    // state は URL 監視で自動反映
  };

  const subjectImage = subject?.image || profile?.image || DEFAULT_AVATAR;
  const subjectName  = subject?.nickname || profile?.nickname || t("Connection2") || "未設定";
  const subjectCreated = useMemo(() => {
    const c = subject?.created_at || profile?.created_at;
    if (!c) return "";
    try {
      const d = new Date(c);
      return Number.isNaN(d.getTime()) ? String(c) : d.toLocaleDateString();
    } catch { return String(c); }
  }, [subject?.created_at, profile?.created_at]);

    // ★ PeopleCard 用に整形（{ profile } があれば中身を渡す）
    const handleFollowChange = useCallback((changedUserId, nextFollowing) => {
      setItems((prev) =>
        prev.map((u) => {
          const pe = u?.profile || u;
          const uid = pe?.userId || pe?.id || pe?.userProfile;
          if (uid !== changedUserId) return u;
          const updated = { ...pe, isFollowedByMe: nextFollowing };
          return u?.profile ? { ...u, profile: updated } : updated;
        })
      );
    }, []);
    const list = items.map((u, i) => {
      const pe = u?.profile || u;
      return (
        <PeopleCard
          key={(pe?.userId || pe?.id || i) + `-${tab}`}
          item={pe}
          onFollowChange={handleFollowChange}
        />
      );
    });

  return (
    <Fragment>
      <Header />
      <Leftnav />

      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left ps-2 pe-2">
            <div className="row">
              {/* プロフィールヘッダー（見た目は既存の Following に合わせる） */}
              <div className="col-xl-12 mb-3">
                <div className="card w-100 border-0 p-0 pt-10 mt-5 bg-white shadow-xss rounded-xxl">
                  <div className="card-body p-0 position-relative">
                    <figure
                      className="avatar position-absolute w100 z-index-1"
                      style={{ top: "-40px", left: "30px" }}
                    >
                      <img
                        src={subjectImage}
                        alt="avatar"
                        className="float-right p-1 bg-white rounded-circle w-100"
                        onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                      />
                    </figure>
                    <h4 className="fw-700 font-sm mt-2 mb-lg-5 mb-4 pl-15">
                      {subjectName}
                      {subjectCreated && (
                        <span className="fw-500 font-xssss text-grey-500 mt-1 mb-3 d-block">
                          {subjectCreated}{t("Userpage6")}
                        </span>
                      )}
                    </h4>

                    {/* 右上の設定ボタン（自分のページ時のみ表示してもOK） */}
                    {isAuthenticated && (!userIdFromUrl || userIdFromUrl === me?.userId) && (
                      <div className="position-absolute bottom-8 mb-2 right-15">
                        <Link to="/settings">
                          <button className="pointer mt-0 btn pt-2 pb-2 ps-3 pe-3 lh-24 ls-3 d-inline-block rounded-xl font-xsssss fw-700 ls-lg text-grey-600 d-flex">
                            <i className="feather-settings text-grey-500 mt-0 font-lg"></i>
                          </button>
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* タブ（URLでトグル） */}
                  <div className="card-body d-block w-100 shadow-none mb-0 p-0 border-top-xs">
                  <ul className="d-flex ps-4" role="tablist">
                  <li className="me-3" role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === "following"}
                      className={`${tab === "following" ? "fw-700" : "fw-500"} font-xssss text-grey-700 pt-3 pb-3 ls-1 bg-transparent border-0`}
                      onClick={() => setTabAndPush("following")}
                    >
                      {t("Followed2")}
                    </button>
                  </li>
                  <li className="me-3" role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === "followers"}
                      className={`${tab === "followers" ? "fw-700" : "fw-500"} font-xssss text-grey-700 pt-3 pb-3 ls-1 bg-transparent border-0`}
                      onClick={() => setTabAndPush("followers")}
                    >
                      {t("Followed1")}
                    </button>
                  </li>
                </ul>
                  </div>
                </div>
              </div>

              {/* 一覧 */}
              <div className="col-xl-12 col-xxl-12 col-lg-12">
                <div className="row ps-2 pe-2">
                  {items.length > 0 ? (
                    <div className="row ps-2 pe-2">{list}</div>
                  ) : (
                    !isLoading && (
                      <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3 text-center text-grey-500">
                        {t("Followed3")}
                      </div>
                    )
                  )}
                </div>

                {/* 無限スクロールターゲット */}
                <div ref={loaderRef} style={{ height: 20, margin: "0 auto" }}></div>

                {/* ローディング */}
                {isLoading && (
                  <div className="card-body p-0 mb-3">
                    <div className="card w-100 shadow-xss rounded-xxl border-0 p-4 mb-3">
                      <div className="snippet mt-2 ms-auto me-auto" data-title=".dot-typing">
                        <div className="stage"><div className="dot-typing"></div></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />
    </Fragment>
  );
};

export default Connections;
